import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { FormlyOptionsService, FormlyService } from '@nx-ahs/formly/shared';
import { BuilderService } from '../../../services/builder.service';
import { FormlyBuilderApiService } from '../../../services/formly-builder-api.service';

interface ExpressionCondition {
	field: string;
	operator: string;
	value: string;
	logic: 'AND' | 'OR';
}

@Component({
	selector: 'nx-ahs-field-card',
	templateUrl: './field-card.component.html',
	styleUrl: './field-card.component.scss',
	standalone: false
})
export class FieldCardComponent implements OnChanges {
	@Input() field: FormlyFieldConfig;
	@Input() path: number[] = [];
	@Input() isSelected: boolean = false;
	@Input() allFields: FormlyFieldConfig[] = []; // All fields from the form for expression builder
	@Output() fieldSelected = new EventEmitter<{ field: FormlyFieldConfig; path: number[] }>();
	@Output() fieldUpdated = new EventEmitter<FormlyFieldConfig>();
	@Output() deleteField = new EventEmitter<void>();
	@Output() duplicateField = new EventEmitter<void>();
	@Output() moveUp = new EventEmitter<void>();
	@Output() moveDown = new EventEmitter<void>();

	// Properties form
	propertiesForm: FormGroup;
	isExpanded = false;
	isChildrenExpanded = false; // For expanding/collapsing nested fields

	// API Testing
	apiTesting = false;
	apiTestResult: any = null;
	apiTestError: string | null = null;
	showApiTestResults = false;

	// Expression Builder
	showExpressionBuilder = true; // Default to visual builder
	expressionConditions: ExpressionCondition[] = [];
	availableFields: string[] = [];

	operators = [
		{ value: '===', label: 'equals' },
		{ value: '!==', label: 'not equals' },
		{ value: '>', label: 'greater than' },
		{ value: '<', label: 'less than' },
		{ value: '>=', label: 'greater than or equals' },
		{ value: '<=', label: 'less than or equals' },
		{ value: 'truthy', label: 'is true/has value' },
		{ value: 'falsy', label: 'is false/empty' }
	];

	// Nested Script Configuration
	availableScripts: any[] = [];
	selectedScripts: { scriptId: number; name?: string; description?: string }[] = [];
	scriptSearchTerm: string = '';
	loadingScripts = false;

	// Available field types for adding to groups/repeaters
	availableFieldTypes = [
		{ type: 'input', label: 'Text Input', icon: 'text_fields' },
		{ type: 'textarea', label: 'Text Area', icon: 'notes' },
		{ type: 'select', label: 'Select', icon: 'arrow_drop_down_circle' },
		{ type: 'checkbox', label: 'Checkbox', icon: 'check_box' },
		{ type: 'radio', label: 'Radio', icon: 'radio_button_checked' },
		{ type: 'date', label: 'Date', icon: 'event' },
		{ type: 'hidden', label: 'Hidden', icon: 'visibility_off' },
		{ type: 'static', label: 'Static Text', icon: 'article' },
		{ type: 'richtext', label: 'Rich Text', icon: 'format_color_text' },
		{ type: 'group', label: 'Group', icon: 'view_agenda' },
		{ type: 'repeater', label: 'Repeater', icon: 'repeat' }
	];

	constructor(
		private http: HttpClient,
		private formlyOptionsService: FormlyOptionsService,
		private formlyService: FormlyService,
		private builderApiService: FormlyBuilderApiService,
		private builderService: BuilderService
	) {
		this.initializeForm();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['field'] && this.field) {
			this.populateForm();

			// Auto-expand groups, repeaters, and nested scripts to show nested fields
			if (this.isGroup || this.isRepeater || this.isNestedScript) {
				this.isChildrenExpanded = true;
			}

			// Load scripts for nested script fields
			if (this.isNestedScript) {
				this.loadAvailableScripts();
			}
		}

		// Update available fields when allFields input changes
		if (changes['allFields']) {
			this.updateAvailableFields();

			// Parse existing expression if we have one and no conditions yet
			const existingExpression = this.propertiesForm.get('hideExpression')?.value;
			if (existingExpression && this.expressionConditions.length === 0) {
				this.parseExpressionToConditions(existingExpression);
			}
		}
	}

	get fieldIcon(): string {
		const type = typeof this.field?.type === 'string' ? this.field.type : '';
		const typeMap: Record<string, string> = {
			'mat-formly-input': 'text_fields',
			input: 'text_fields',
			'mat-formly-textarea': 'notes',
			textarea: 'notes',
			'mat-formly-select': 'arrow_drop_down_circle',
			select: 'arrow_drop_down_circle',
			'mat-formly-check': 'check_box',
			checkbox: 'check_box',
			'mat-formly-radio': 'radio_button_checked',
			radio: 'radio_button_checked',
			'mat-formly-date': 'event',
			date: 'event',
			'mat-formly-static': 'article',
			static: 'article',
			'mat-formly-hidden': 'visibility_off',
			hidden: 'visibility_off',
			'mat-formly-richtext': 'format_color_text',
			richtext: 'format_color_text',
			'mat-formly-chips': 'label',
			chips: 'label',
			'mat-formly-chips-autocomplete': 'local_offer',
			'chips-autocomplete': 'local_offer',
			'mat-formly-address': 'location_on',
			address: 'location_on',
			'mat-formly-file-upload': 'cloud_upload',
			'file-upload': 'cloud_upload',
			'mat-formly-group': 'view_agenda',
			'formly-group': 'view_agenda',
			group: 'view_agenda',
			'mat-formly-repeater': 'repeat',
			'formly-repeater': 'repeat',
			repeater: 'repeat',
			'mat-formly-nested-script': 'list_alt',
			'nested-script': 'list_alt',
			'script-item': 'description', // Individual script items get a document icon
			'mat-formly-setter': 'settings',
			setter: 'settings'
		};
		return typeMap[type] || 'help_outline';
	}

	get fieldLabel(): string {
		const key = this.field?.key;
		const keyString = Array.isArray(key) ? key.join('.') : String(key || '');
		return this.field?.props?.label || keyString || this.getTypeLabel();
	}

	get fieldType(): string {
		return this.getTypeLabel();
	}

	get fieldKey(): string {
		const key = this.field?.key;
		if (!key) return 'No Key';
		return Array.isArray(key) ? key.join('.') : String(key);
	}

	get hasChildren(): boolean {
		const hasFieldGroupOrArray = !!(this.field?.fieldGroup?.length || this.field?.fieldArray);
		const hasNestedScripts =
			this.isNestedScript &&
			this.field?.props &&
			Array.isArray(this.field.props['scripts']) &&
			this.field.props['scripts'].length > 0;
		return hasFieldGroupOrArray || hasNestedScripts;
	}

	get childFields(): FormlyFieldConfig[] {
		if (this.field?.fieldGroup && this.field.fieldGroup.length > 0) {
			return this.field.fieldGroup;
		}
		if (this.field?.fieldArray) {
			const fieldArray = this.field.fieldArray;
			const config = typeof fieldArray === 'function' ? fieldArray({}) : fieldArray;
			if (config && config.fieldGroup) {
				return config.fieldGroup;
			}
		}
		// Handle nested scripts
		if (this.isNestedScript && this.field?.props && Array.isArray(this.field.props['scripts'])) {
			const scripts = this.field.props['scripts'] as any[];
			return scripts.map(
				(script, index) =>
					({
						key: `script-${script.scriptId || index}`,
						type: 'script-item', // Use different type for child items
						props: {
							label: script.name || `Script ${script.scriptId || index}`,
							...script,
							scriptId: script.scriptId,
							name: script.name,
							description: script.description
						}
					}) as FormlyFieldConfig
			);
		}
		return [];
	}

	get childCount(): number {
		return this.childFields.length;
	}

	get isGroup(): boolean {
		const type = typeof this.field?.type === 'string' ? this.field.type : '';
		return type === 'mat-formly-group' || type === 'formly-group' || type === 'group';
	}

	get isRepeater(): boolean {
		const type = typeof this.field?.type === 'string' ? this.field.type : '';
		return type === 'mat-formly-repeater' || type === 'formly-repeater' || type === 'repeater';
	}

	get isNestedScript(): boolean {
		const type = typeof this.field?.type === 'string' ? this.field.type : '';
		return type === 'mat-formly-nested-script' || type === 'nested-script';
	}

	get isRequired(): boolean {
		return !!this.field?.props?.required;
	}

	get hasApi(): boolean {
		return !!this.field?.props?.api?.url;
	}

	get apiTooltip(): string {
		const api = this.field?.props?.api;
		if (!api?.url) return '';
		return `API: ${api.url}\nLabel: ${api.itemFieldLabel || 'N/A'}\nValue: ${api.itemFieldValue || 'N/A'}`;
	}

	get hasDependencies(): boolean {
		// Check if there are actual expressions with values
		const hasHideExpression = !!(this.field?.expressions?.hide || this.field?.hideExpression);
		const hasOtherExpressions =
			this.field?.expressions &&
			Object.keys(this.field.expressions).length > 0 &&
			Object.keys(this.field.expressions).some((key) => this.field.expressions[key] != null);

		return hasHideExpression || hasOtherExpressions;
	}

	get dependencyTooltip(): string {
		const parts: string[] = [];

		// Check for hideExpression
		if (this.field?.expressions?.hide) {
			const hideExpr =
				typeof this.field.expressions.hide === 'function'
					? this.field.expressions.hide.toString()
					: String(this.field.expressions.hide);
			const referencedFields = this.extractFieldReferences(hideExpr);
			parts.push(`Hide Expression: ${this.field.expressions.hide}`);
			if (referencedFields.length > 0) {
				parts.push(`Depends on: ${referencedFields.join(', ')}`);
			}
		} else if (this.field?.hideExpression) {
			const hideExpr =
				typeof this.field.hideExpression === 'function'
					? this.field.hideExpression.toString()
					: String(this.field.hideExpression);
			const referencedFields = this.extractFieldReferences(hideExpr);
			parts.push(`Hide Expression: ${this.field.hideExpression}`);
			if (referencedFields.length > 0) {
				parts.push(`Depends on: ${referencedFields.join(', ')}`);
			}
		}

		// Check for other expressions
		if (this.field?.expressions) {
			const exprKeys = Object.keys(this.field.expressions).filter((k) => k !== 'hide');
			if (exprKeys.length > 0) {
				parts.push(`Other expressions: ${exprKeys.join(', ')}`);
			}
		}

		return parts.length > 0 ? parts.join('\n') : 'Has conditional logic';
	}

	private extractFieldReferences(expression: string): string[] {
		const references = new Set<string>();

		// Match model.fieldName patterns
		const modelMatches = expression.match(/model\.(\w+)/g);
		if (modelMatches) {
			modelMatches.forEach((match) => {
				const fieldName = match.replace('model.', '');
				references.add(fieldName);
			});
		}

		// Match formState references
		const formStateMatches = expression.match(/formState\.(\w+)/g);
		if (formStateMatches) {
			formStateMatches.forEach((match) => {
				const fieldName = match.replace('formState.', '');
				references.add(fieldName);
			});
		}

		return Array.from(references);
	}

	private getTypeLabel(): string {
		const type = this.field?.type || '';
		const typeString = typeof type === 'string' ? type : '';
		const labelMap: Record<string, string> = {
			'mat-formly-input': 'Input',
			input: 'Input',
			'mat-formly-textarea': 'Text Area',
			textarea: 'Text Area',
			'mat-formly-select': 'Select',
			select: 'Select',
			'mat-formly-check': 'Checkbox',
			checkbox: 'Checkbox',
			'mat-formly-radio': 'Radio',
			radio: 'Radio',
			'mat-formly-date': 'Date',
			date: 'Date',
			'mat-formly-static': 'Static',
			static: 'Static',
			'mat-formly-hidden': 'Hidden',
			hidden: 'Hidden',
			'mat-formly-richtext': 'Rich Text',
			richtext: 'Rich Text',
			'mat-formly-chips': 'Chips',
			chips: 'Chips',
			'mat-formly-chips-autocomplete': 'Chips Autocomplete',
			'chips-autocomplete': 'Chips Autocomplete',
			'mat-formly-address': 'Address',
			address: 'Address',
			'mat-formly-file-upload': 'File Upload',
			'file-upload': 'File Upload',
			'mat-formly-group': 'Group',
			'formly-group': 'Group',
			group: 'Group',
			'mat-formly-repeater': 'Repeater',
			'formly-repeater': 'Repeater',
			repeater: 'Repeater',
			'mat-formly-nested-script': 'Nested Script',
			'nested-script': 'Nested Script',
			'script-item': 'Script',
			'mat-formly-setter': 'Setter',
			setter: 'Setter'
		};
		return labelMap[typeString] || typeString;
	}

	onFieldClick(): void {
		this.fieldSelected.emit({ field: this.field, path: this.path });
	}

	onDelete(event: Event): void {
		event.stopPropagation();
		this.deleteField.emit();
	}

	onDuplicate(event: Event): void {
		event.stopPropagation();
		this.duplicateField.emit();
	}

	onMoveUp(event: Event): void {
		event.stopPropagation();
		this.moveUp.emit();
	}

	onMoveDown(event: Event): void {
		event.stopPropagation();
		this.moveDown.emit();
	}

	toggleExpand(event: Event): void {
		event.stopPropagation();
		this.isExpanded = !this.isExpanded;
		if (this.isExpanded) {
			this.populateForm();

			// Update available fields and parse existing expression for visual builder
			if (this.showExpressionBuilder) {
				this.updateAvailableFields();

				const existingExpression = this.propertiesForm.get('hideExpression')?.value;
				if (existingExpression) {
					this.parseExpressionToConditions(existingExpression);
				} else if (this.expressionConditions.length === 0) {
					// Add one empty condition if no expression exists
					this.addCondition();
				}
			}
		}
	}

	toggleChildren(event: Event): void {
		event.stopPropagation();
		this.isChildrenExpanded = !this.isChildrenExpanded;
	}

	getChildPath(childIndex: number): number[] {
		return [...this.path, childIndex];
	}

	onChildFieldUpdated(childIndex: number, updatedChild: FormlyFieldConfig): void {
		if (this.field.fieldGroup) {
			this.field.fieldGroup[childIndex] = updatedChild;
			this.fieldUpdated.emit(this.field);
		} else if (this.field.fieldArray) {
			const fieldArray = this.field.fieldArray;
			const config = typeof fieldArray === 'function' ? fieldArray({}) : fieldArray;
			if (config && config.fieldGroup) {
				config.fieldGroup[childIndex] = updatedChild;
				this.fieldUpdated.emit(this.field);
			}
		} else if (this.isNestedScript && this.field.props && Array.isArray(this.field.props['scripts'])) {
			// Update the script in the scripts array
			const scripts = this.field.props['scripts'] as any[];
			if (childIndex >= 0 && childIndex < scripts.length) {
				// Merge the updated properties back to the script object
				scripts[childIndex] = {
					...scripts[childIndex],
					...updatedChild.props,
					scriptId: updatedChild.props?.scriptId || scripts[childIndex].scriptId,
					name: updatedChild.props?.label || updatedChild.props?.name || scripts[childIndex].name
				};
				this.fieldUpdated.emit(this.field);
			}
		}
	}

	onChildDeleted(childIndex: number): void {
		if (this.field.fieldGroup) {
			this.field.fieldGroup.splice(childIndex, 1);
			this.fieldUpdated.emit(this.field);
		} else if (this.field.fieldArray) {
			const fieldArray = this.field.fieldArray;
			const config = typeof fieldArray === 'function' ? fieldArray({}) : fieldArray;
			if (config && config.fieldGroup) {
				config.fieldGroup.splice(childIndex, 1);
				this.fieldUpdated.emit(this.field);
			}
		} else if (this.isNestedScript && this.field.props && Array.isArray(this.field.props['scripts'])) {
			// Delete the script from the scripts array
			const scripts = this.field.props['scripts'] as any[];
			scripts.splice(childIndex, 1);
			this.fieldUpdated.emit(this.field);
		}
	}

	onChildDuplicated(childIndex: number): void {
		if (this.field.fieldGroup) {
			const duplicate = JSON.parse(JSON.stringify(this.field.fieldGroup[childIndex]));
			this.field.fieldGroup.splice(childIndex + 1, 0, duplicate);
			this.fieldUpdated.emit(this.field);
		} else if (this.field.fieldArray) {
			const fieldArray = this.field.fieldArray;
			const config = typeof fieldArray === 'function' ? fieldArray({}) : fieldArray;
			if (config && config.fieldGroup) {
				const duplicate = JSON.parse(JSON.stringify(config.fieldGroup[childIndex]));
				config.fieldGroup.splice(childIndex + 1, 0, duplicate);
				this.fieldUpdated.emit(this.field);
			}
		} else if (this.isNestedScript && this.field.props && Array.isArray(this.field.props['scripts'])) {
			// Duplicate the script in the scripts array
			const scripts = this.field.props['scripts'] as any[];
			const duplicate = JSON.parse(JSON.stringify(scripts[childIndex]));
			scripts.splice(childIndex + 1, 0, duplicate);
			this.fieldUpdated.emit(this.field);
		}
	}

	onChildMoveUp(childIndex: number): void {
		if (childIndex > 0) {
			if (this.field.fieldGroup) {
				const temp = this.field.fieldGroup[childIndex];
				this.field.fieldGroup[childIndex] = this.field.fieldGroup[childIndex - 1];
				this.field.fieldGroup[childIndex - 1] = temp;
				this.fieldUpdated.emit(this.field);
			} else if (this.field.fieldArray) {
				const fieldArray = this.field.fieldArray;
				const config = typeof fieldArray === 'function' ? fieldArray({}) : fieldArray;
				if (config && config.fieldGroup) {
					const temp = config.fieldGroup[childIndex];
					config.fieldGroup[childIndex] = config.fieldGroup[childIndex - 1];
					config.fieldGroup[childIndex - 1] = temp;
					this.fieldUpdated.emit(this.field);
				}
			} else if (this.isNestedScript && this.field.props && Array.isArray(this.field.props['scripts'])) {
				// Move script up in the scripts array
				const scripts = this.field.props['scripts'] as any[];
				const temp = scripts[childIndex];
				scripts[childIndex] = scripts[childIndex - 1];
				scripts[childIndex - 1] = temp;
				this.fieldUpdated.emit(this.field);
			}
		}
	}

	onChildMoveDown(childIndex: number): void {
		if (this.field.fieldGroup && childIndex < this.field.fieldGroup.length - 1) {
			const temp = this.field.fieldGroup[childIndex];
			this.field.fieldGroup[childIndex] = this.field.fieldGroup[childIndex + 1];
			this.field.fieldGroup[childIndex + 1] = temp;
			this.fieldUpdated.emit(this.field);
		} else if (this.field.fieldArray) {
			const fieldArray = this.field.fieldArray;
			const config = typeof fieldArray === 'function' ? fieldArray({}) : fieldArray;
			if (config && config.fieldGroup && childIndex < config.fieldGroup.length - 1) {
				const temp = config.fieldGroup[childIndex];
				config.fieldGroup[childIndex] = config.fieldGroup[childIndex + 1];
				config.fieldGroup[childIndex + 1] = temp;
				this.fieldUpdated.emit(this.field);
			}
		} else if (this.isNestedScript && this.field.props && Array.isArray(this.field.props['scripts'])) {
			// Move script down in the scripts array
			const scripts = this.field.props['scripts'] as any[];
			if (childIndex < scripts.length - 1) {
				const temp = scripts[childIndex];
				scripts[childIndex] = scripts[childIndex + 1];
				scripts[childIndex + 1] = temp;
				this.fieldUpdated.emit(this.field);
			}
		}
	}

	private initializeForm(): void {
		this.propertiesForm = new FormGroup({
			key: new FormControl(''),
			label: new FormControl(''),
			placeholder: new FormControl(''),
			description: new FormControl(''),
			hint: new FormControl(''),
			required: new FormControl(false),
			disabled: new FormControl(false),
			exclude: new FormControl(false),
			className: new FormControl(''),
			hideExpression: new FormControl(''),
			defaultValue: new FormControl(''),
			inputType: new FormControl('text'),
			// API Configuration
			apiUrl: new FormControl(''),
			apiItemFieldLabel: new FormControl(''),
			apiItemFieldValue: new FormControl(''),
			apiMethod: new FormControl(null),
			apiRefDataName: new FormControl(''),
			apiOverride: new FormControl(false),
			// Style Configuration
			styleWidth: new FormControl(''),
			styleBackgroundColor: new FormControl(''),
			styleColor: new FormControl(''),
			styleFontSize: new FormControl(''),
			stylePadding: new FormControl(''),
			styleMargin: new FormControl(''),
			styleBorder: new FormControl(''),
			customStyles: new FormControl('')
		});
	}

	private populateForm(): void {
		if (!this.field) return;

		const props = this.field.props || {};
		const api = props.api || {};
		const style = props.style || {};

		this.propertiesForm.patchValue(
			{
				key: this.field.key || '',
				label: props.label || '',
				placeholder: props.placeholder || '',
				description: props.description || '',
				hint: props.hint || '',
				required: props.required || false,
				disabled: props.disabled || false,
				exclude: props.exclude || false,
				className: this.field.className || '',
				hideExpression: this.field.hideExpression || '',
				defaultValue: this.field.defaultValue || '',
				inputType: props.inputType || 'text',
				// API Configuration
				apiUrl: api.url || '',
				apiItemFieldLabel: api.itemFieldLabel || '',
				apiItemFieldValue: api.itemFieldValue || '',
				apiMethod: api.method || null,
				apiRefDataName: api.refDataName || '',
				apiOverride: api.override || false,
				// Style Configuration
				styleWidth: style.width || '',
				styleBackgroundColor: style.backgroundColor || '',
				styleColor: style.color || '',
				styleFontSize: style.fontSize || '',
				stylePadding: style.padding || '',
				styleMargin: style.margin || '',
				styleBorder: style.border || '',
				customStyles: props.customStyles || ''
			},
			{ emitEvent: false }
		);

		// Load nested scripts if applicable
		if (this.isNestedScript && props.scripts) {
			this.selectedScripts = [...props.scripts];
		} else {
			this.selectedScripts = [];
		}
	}

	onApplyChanges(event: Event): void {
		event.stopPropagation();
		if (!this.field) return;

		const formValue = this.propertiesForm.value;
		const updatedField: FormlyFieldConfig = {
			...this.field,
			key: formValue.key || this.field.key,
			className: formValue.className || undefined,
			hideExpression: formValue.hideExpression || undefined,
			defaultValue: formValue.defaultValue || undefined,
			props: {
				...this.field.props,
				label: formValue.label || null,
				placeholder: formValue.placeholder || null,
				description: formValue.description || null,
				hint: formValue.hint || null,
				required: formValue.required || null,
				disabled: formValue.disabled || null,
				exclude: formValue.exclude || null
			}
		};

		// Add inputType for input fields
		if (this.showInputTypeField) {
			updatedField.props = {
				...updatedField.props,
				inputType: formValue.inputType || 'text'
			};
		}

		// Handle API Configuration
		if (formValue.apiUrl) {
			updatedField.props = {
				...updatedField.props,
				api: {
					url: formValue.apiUrl,
					itemFieldLabel: formValue.apiItemFieldLabel || null,
					itemFieldValue: formValue.apiItemFieldValue || null,
					method: formValue.apiMethod || null,
					refDataName: formValue.apiRefDataName || null,
					override: formValue.apiOverride || false,
					options: null
				}
			};
		} else if (updatedField.props?.api) {
			// Remove API config if URL is cleared
			delete updatedField.props.api;
		}

		// Handle Nested Scripts Configuration
		if (this.isNestedScript) {
			updatedField.props = {
				...updatedField.props,
				scripts: this.selectedScripts.length > 0 ? [...this.selectedScripts] : []
			};
		}

		// Handle Style Configuration
		const styleObj: any = {};
		if (formValue.styleWidth) styleObj.width = formValue.styleWidth;
		if (formValue.styleBackgroundColor) styleObj.backgroundColor = formValue.styleBackgroundColor;
		if (formValue.styleColor) styleObj.color = formValue.styleColor;
		if (formValue.styleFontSize) styleObj.fontSize = formValue.styleFontSize;
		if (formValue.stylePadding) styleObj.padding = formValue.stylePadding;
		if (formValue.styleMargin) styleObj.margin = formValue.styleMargin;
		if (formValue.styleBorder) styleObj.border = formValue.styleBorder;

		// Add custom styles if provided
		if (formValue.customStyles) {
			updatedField.props = {
				...updatedField.props,
				customStyles: formValue.customStyles
			};
		}

		// Apply style object if any styles are set
		if (Object.keys(styleObj).length > 0) {
			updatedField.props = {
				...updatedField.props,
				style: styleObj
			};
		}

		// Clean up null/undefined values
		Object.keys(updatedField.props).forEach((key) => {
			if (
				updatedField.props[key] === null ||
				updatedField.props[key] === undefined ||
				updatedField.props[key] === ''
			) {
				delete updatedField.props[key];
			}
		});

		this.fieldUpdated.emit(updatedField);
		this.isExpanded = false;
	}

	onCancelChanges(event: Event): void {
		event.stopPropagation();
		this.populateForm();
		this.isExpanded = false;
	}

	get showInputTypeField(): boolean {
		const type = typeof this.field?.type === 'string' ? this.field.type : '';
		return type === 'mat-formly-input';
	}

	get showOptionsField(): boolean {
		const type = typeof this.field?.type === 'string' ? this.field.type : '';
		return ['mat-formly-select', 'mat-formly-radio', 'mat-formly-check'].includes(type);
	}

	get referencedFields(): string[] {
		const hideExpr = this.propertiesForm.get('hideExpression')?.value || '';
		if (!hideExpr) return [];
		return this.extractFieldReferences(hideExpr);
	}

	testApiEndpoint(event: Event): void {
		event.stopPropagation();
		const apiUrl = this.propertiesForm.get('apiUrl')?.value;

		if (!apiUrl) {
			this.apiTestError = 'Please enter an API URL';
			this.showApiTestResults = true;
			return;
		}

		// Reset previous results
		this.apiTesting = true;
		this.apiTestResult = null;
		this.apiTestError = null;
		this.showApiTestResults = true;

		// Construct full URL using FormlyOptionsService API URL (same as form fields use)
		const fullUrl = apiUrl.startsWith('http')
			? apiUrl
			: `${this.formlyOptionsService.apiUrl}${apiUrl.startsWith('/') ? apiUrl.substring(1) : apiUrl}`;

		// Make HTTP request
		this.http
			.get(fullUrl)
			.pipe(
				catchError((error) => {
					this.apiTesting = false;
					this.apiTestError = `Error: ${error.status} - ${error.statusText || error.message}`;
					return of(null);
				})
			)
			.subscribe((response) => {
				this.apiTesting = false;

				if (response) {
					this.apiTestResult = response;

					// Validate response structure
					const labelField = this.propertiesForm.get('apiItemFieldLabel')?.value;
					const valueField = this.propertiesForm.get('apiItemFieldValue')?.value;

					if (Array.isArray(response) && response.length > 0) {
						const sampleItem = response[0];
						if (labelField && !Object.prototype.hasOwnProperty.call(sampleItem, labelField)) {
							this.apiTestError = `Warning: Label field "${labelField}" not found in response`;
						}
						if (valueField && !Object.prototype.hasOwnProperty.call(sampleItem, valueField)) {
							this.apiTestError = this.apiTestError
								? `${this.apiTestError}. Value field "${valueField}" not found`
								: `Warning: Value field "${valueField}" not found in response`;
						}
					}
				}
			});
	}

	closeApiTestResults(event: Event): void {
		event.stopPropagation();
		this.showApiTestResults = false;
		this.apiTestResult = null;
		this.apiTestError = null;
	}

	get apiTestResultJson(): string {
		if (!this.apiTestResult) return '';
		return JSON.stringify(this.apiTestResult, null, 2);
	}

	get apiTestResultCount(): number {
		if (Array.isArray(this.apiTestResult)) {
			return this.apiTestResult.length;
		}
		return 0;
	}

	// Expression Builder Methods
	toggleExpressionBuilder(event: Event): void {
		event.stopPropagation();
		this.showExpressionBuilder = !this.showExpressionBuilder;

		if (this.showExpressionBuilder) {
			// Parse existing expression into conditions
			const existingExpression = this.propertiesForm.get('hideExpression')?.value;
			if (existingExpression) {
				this.parseExpressionToConditions(existingExpression);
			}
			// Get available fields from the parent (we'll need to pass this from parent)
			this.updateAvailableFields();
		}
	}

	addCondition(): void {
		this.expressionConditions.push({
			field: '',
			operator: '===',
			value: '',
			logic: 'AND'
		});
	}

	removeCondition(index: number): void {
		this.expressionConditions.splice(index, 1);
		this.buildExpressionFromConditions();
	}

	buildExpressionFromConditions(): void {
		if (this.expressionConditions.length === 0) {
			this.propertiesForm.patchValue({ hideExpression: '' });
			return;
		}

		const expressions = this.expressionConditions.map((condition, index) => {
			let expr = '';

			// Add logic operator if not the first condition
			if (index > 0) {
				expr = condition.logic === 'AND' ? ' && ' : ' || ';
			}

			// Build the condition expression
			if (condition.operator === 'truthy') {
				expr += `model.${condition.field}`;
			} else if (condition.operator === 'falsy') {
				expr += `!model.${condition.field}`;
			} else {
				// For string values, wrap in quotes
				const value = isNaN(Number(condition.value)) ? `'${condition.value}'` : condition.value;
				expr += `model.${condition.field} ${condition.operator} ${value}`;
			}

			return expr;
		});

		const fullExpression = expressions.join('');
		this.propertiesForm.patchValue({ hideExpression: fullExpression });
	}

	private parseExpressionToConditions(expression: string): void {
		// Simple parsing - this is a basic implementation
		// For more complex expressions, a proper parser would be needed
		this.expressionConditions = [];

		// Split by && or ||
		const parts = expression.split(/(\s*&&\s*|\s*\|\|\s*)/);

		for (let i = 0; i < parts.length; i++) {
			const part = parts[i].trim();

			// Skip logic operators
			if (part === '&&' || part === '||') continue;

			// Try to parse the condition
			const condition = this.parseCondition(part);
			if (condition) {
				// Set logic from previous operator
				if (i > 0 && parts[i - 1]) {
					condition.logic = parts[i - 1].includes('&&') ? 'AND' : 'OR';
				} else {
					condition.logic = 'AND';
				}
				this.expressionConditions.push(condition);
			}
		}

		// If parsing failed, add one empty condition
		if (this.expressionConditions.length === 0) {
			this.addCondition();
		}
	}

	private parseCondition(expr: string): ExpressionCondition | null {
		// Remove model. prefix
		expr = expr.replace(/model\./g, '');

		// Check for truthy/falsy (but not if it's part of != operator)
		if (expr.startsWith('!') && !expr.includes('!=')) {
			return {
				field: expr.substring(1).trim(),
				operator: 'falsy',
				value: '',
				logic: 'AND'
			};
		}

		// Check for operators - order matters! Check longer operators first
		const operators = ['===', '!==', '>=', '<=', '!=', '==', '>', '<'];
		for (const op of operators) {
			if (expr.includes(op)) {
				const [field, value] = expr.split(op).map((s) => s.trim());
				// Normalize != to !== and == to ===
				let normalizedOp = op;
				if (op === '!=') normalizedOp = '!==';
				if (op === '==') normalizedOp = '===';

				return {
					field: field,
					operator: normalizedOp,
					value: value.replace(/['"]/g, ''), // Remove quotes
					logic: 'AND'
				};
			}
		}

		// If no operator found, treat as truthy
		return {
			field: expr.trim(),
			operator: 'truthy',
			value: '',
			logic: 'AND'
		};
	}

	private updateAvailableFields(): void {
		// Extract all field keys from allFields input
		this.availableFields = this.getAllFieldKeys(this.allFields);
	}

	private getAllFieldKeys(fields: FormlyFieldConfig[]): string[] {
		const keys: string[] = [];

		fields.forEach((field) => {
			if (field.key) {
				const key = Array.isArray(field.key) ? field.key.join('.') : String(field.key);
				// Don't include the current field itself
				const currentKey = Array.isArray(this.field?.key) ? this.field.key.join('.') : String(this.field?.key);
				if (key !== currentKey) {
					keys.push(key);
				}
			}

			// Recursively get keys from nested fields
			if (field.fieldGroup) {
				keys.push(...this.getAllFieldKeys(field.fieldGroup));
			}
		});

		return keys;
	}

	useExpressionBuilder(): void {
		this.showExpressionBuilder = true;

		// Update available fields from allFields input
		this.updateAvailableFields();

		// Parse existing expression if present
		const existingExpression = this.propertiesForm.get('hideExpression')?.value;
		if (existingExpression && this.expressionConditions.length === 0) {
			this.parseExpressionToConditions(existingExpression);
		} else if (this.expressionConditions.length === 0) {
			this.addCondition();
		}
	}

	useRawExpression(): void {
		this.showExpressionBuilder = false;
	}

	// Nested Script Methods
	get filteredAvailableScripts(): any[] {
		if (!this.scriptSearchTerm) {
			return this.availableScripts.filter(
				(script) => !this.selectedScripts.some((s) => s.scriptId === script.scriptId)
			);
		}
		const searchLower = this.scriptSearchTerm.toLowerCase();
		return this.availableScripts.filter(
			(script) =>
				!this.selectedScripts.some((s) => s.scriptId === script.scriptId) &&
				(script.name?.toLowerCase().includes(searchLower) ||
					script.scriptId?.toString().includes(searchLower) ||
					script.description?.toLowerCase().includes(searchLower))
		);
	}

	async loadAvailableScripts(): Promise<void> {
		if (this.availableScripts.length > 0) return; // Already loaded

		this.loadingScripts = true;
		try {
			this.availableScripts = await this.builderApiService.getAllScripts();
			// Filter out deleted scripts
			this.availableScripts = this.availableScripts.filter((script) => !script.isDeleted);
		} catch (error) {
			console.error('Error loading scripts:', error);
		} finally {
			this.loadingScripts = false;
		}
	}

	addScript(script: any, event: Event): void {
		event.stopPropagation();
		if (!this.selectedScripts.some((s) => s.scriptId === script.scriptId)) {
			this.selectedScripts.push({
				scriptId: script.scriptId,
				name: script.name || `Script ${script.scriptId}`,
				description: script.description || ''
			});
			this.scriptSearchTerm = ''; // Clear search after adding
		}
	}

	removeScript(index: number, event: Event): void {
		event.stopPropagation();
		this.selectedScripts.splice(index, 1);
	}

	moveScriptUp(index: number, event: Event): void {
		event.stopPropagation();
		if (index > 0) {
			const temp = this.selectedScripts[index];
			this.selectedScripts[index] = this.selectedScripts[index - 1];
			this.selectedScripts[index - 1] = temp;
		}
	}

	moveScriptDown(index: number, event: Event): void {
		event.stopPropagation();
		if (index < this.selectedScripts.length - 1) {
			const temp = this.selectedScripts[index];
			this.selectedScripts[index] = this.selectedScripts[index + 1];
			this.selectedScripts[index + 1] = temp;
		}
	}

	// Group/Repeater Field Management
	addFieldToGroup(fieldType: string, event: Event): void {
		event.stopPropagation();

		if (!this.field) return;

		// Get existing keys for validation using BuilderService
		const existingKeys = this.builderService.getAllFieldKeys(this.allFields);

		// Use BuilderService to open the dialog and create the field
		this.builderService.openAddFieldDialog(fieldType, existingKeys).subscribe((newField) => {
			if (newField) {
				// Add to the group's fieldGroup or repeater's fieldArray
				if (this.isGroup) {
					if (!this.field.fieldGroup) {
						this.field.fieldGroup = [];
					}
					this.field.fieldGroup.push(newField);
				} else if (this.isRepeater) {
					if (!this.field.fieldArray) {
						this.field.fieldArray = { fieldGroup: [] };
					}
					if (typeof this.field.fieldArray === 'object' && this.field.fieldArray !== null) {
						if (!this.field.fieldArray.fieldGroup) {
							this.field.fieldArray.fieldGroup = [];
						}
						this.field.fieldArray.fieldGroup.push(newField);
					}
				}

				// Emit the updated field
				this.fieldUpdated.emit(this.field);
			}
		});
	}
}
